// GUIDED-FLOW (docs/direction/2026-10-28-build-order.md, increment 2; the shared seam of §3.2):
// the guided-flow engine's routes. A flow (definitions in `@companyos/domain-work/flows`) is sent
// to a person about a seat; they fill it step by step (save and resume), see a review and submit.
//
//   flows.list       `GET flows`: runs the caller may open (their own; a manager sees all).
//   flows.send       `POST flows`: a manager sends a flow to a member or a pending invitee.
//   flows.agentSend  `POST agent/flows`: a DE sends "seat setup" to a person in its lanes.
//   flows.get        `GET flows/{id}`: the run as the caller sees it (answers read from the real
//                    fields, cut to what they may see; secure fields only say whether filled).
//   flows.saveStep   `PUT flows/{id}/steps/{step}`: write one step's values to their real fields.
//   flows.submit     `POST flows/{id}/submit`: the review's submit; refused while required
//                    fields are missing (the list says who fills each).
//   flows.cancel     `POST flows/{id}/cancel`: a manager withdraws an open run.
//   flows.helper     `POST flows/{id}/helper`: the side panel's helper answers from the step's
//                    help text plus company context, and may suggest one value the person
//                    accepts with a click; it writes nothing and never sees sealed values.
//   flows.sensitive  `GET flows/{id}/sensitive`: the sealed values, opened for the person, an
//                    owner or finance only.
//   flows.collection `GET collection`: COLLECT-FLOWS progress per person and per role.
import { z } from "zod";
import { FlowKey, FlowRunStatus, InvitationRole, SensitiveValueName } from "../enums";
import { BoundedJson, Timestamp, Uuid } from "../primitives";
import { CompanyItemParams, CompanyParams } from "./common";
import { PersonInvite } from "./org";
import { defineRoute } from "./route";

const base = "/v1/companies/{co}";
const core = { feature: "core" } as const;

export const FlowAudience = z.enum(["manager", "owner", "finance", "assignee"]);

export const FlowAnswer = z
  .union([z.string(), z.number(), z.boolean(), z.array(z.string()), z.null()])
  .meta({ id: "FlowAnswer" });

export const FlowProgress = z
  .object({
    chapters_done: z.int().min(0),
    chapters_total: z.int().min(0),
    percent: z.int().min(0).max(100),
    /** "Complete your seat setup, 3 of 5 chapters" (the chat card and the banner). */
    line: z.string(),
  })
  .meta({ id: "FlowProgress" });

export const FlowRunSummary = z
  .object({
    id: Uuid,
    flow_key: FlowKey,
    title: z.string(),
    seat_id: Uuid,
    seat_title: z.string(),
    status: FlowRunStatus,
    assignee_membership_id: Uuid.nullable(),
    invitation_id: Uuid.nullable(),
    assignee_name: z.string().nullable(),
    progress: FlowProgress,
    created_at: Timestamp,
    updated_at: Timestamp,
    completed_at: Timestamp.nullable(),
  })
  .meta({ id: "FlowRunSummary" });
export type FlowRunSummary = z.infer<typeof FlowRunSummary>;

export const FlowMissing = z
  .object({ step: z.string(), field: z.string(), label: z.string(), fill: z.array(FlowAudience) })
  .meta({ id: "FlowMissing" });

const Choice = z.object({ id: Uuid, label: z.string() });

/** DE onboarding: the lanes the chart gives a DE's seat, for confirmation. */
export const FlowLanes = z
  .object({
    escalate_to: z
      .object({ seat_id: Uuid, title: z.string(), holders: z.array(z.string()) })
      .nullable(),
    peers: z.array(z.object({ seat_id: Uuid, title: z.string() })),
    confirmed_at: Timestamp.nullable(),
  })
  .meta({ id: "FlowLanes" });

export const FlowRunView = FlowRunSummary.extend({
  viewer: z.object({
    audiences: z.array(FlowAudience),
    /** The assignee's access level (narrows what they see). */
    assignee_access: InvitationRole.or(z.literal("owner")).nullable(),
    can_cancel: z.boolean(),
    /** An owner, finance or the person: may open the sealed values. */
    can_open_sensitive: z.boolean(),
  }),
  /** Every field the caller may see, read from its real field; a secure field is `true` once filled. */
  answers: z.record(z.string(), FlowAnswer),
  saved_steps: z.array(z.string()),
  missing: z.array(FlowMissing),
  /**
   * Completion per chapter and step, computed by the server over every answer (the caller's view
   * of the answers is cut, so it cannot recompute this).
   */
  chapters: z.array(
    z.object({
      key: z.string(),
      title: z.string(),
      complete: z.boolean(),
      steps: z.array(
        z.object({ key: z.string(), title: z.string(), complete: z.boolean(), saved: z.boolean() }),
      ),
    }),
  ),
  /** Run-time options of the seat and role fields. */
  options: z.object({
    seats: z.array(Choice),
    roles: z.array(Choice),
  }),
  lanes: FlowLanes.nullable(),
  /** DE onboarding: the DE holding the seat (its How I work editor), else null. */
  de_membership_id: Uuid.nullable(),
  /** Values a template brings for fields not answered yet (shown prefilled, saved on save). */
  prefill: z.record(z.string(), z.string()),
  /** The access level is read-only once the invitation is accepted (People changes it). */
  access_level_locked: z.boolean(),
  /** COLLECT-FLOWS: the uploaded files the visible answers name (empty for other flows). */
  files: z.array(
    z.object({ id: Uuid, file_name: z.string(), mime: z.string(), bytes: z.int().min(0) }),
  ),
  /** The run's version (bumped on every save). */
  version: z.int().min(1),
}).meta({ id: "FlowRunView" });
export type FlowRunView = z.infer<typeof FlowRunView>;

export const FlowListQuery = z.object({
  mine: z.enum(["true", "false"]).optional(),
  seat_id: Uuid.optional(),
  status: FlowRunStatus.optional(),
});

export const FlowRunList = z.object({ data: z.array(FlowRunSummary) }).meta({ id: "FlowRunList" });

export const FlowSend = z
  .strictObject({
    flow_key: FlowKey,
    seat_id: Uuid,
    /** A member to send it to (exactly one of this, `invitation_id` and `invite`). */
    membership_id: Uuid.optional(),
    /** A pending invitation (the invitee fills it once they accept). */
    invitation_id: Uuid.optional(),
    /**
     * Invite someone to the seat and send them the flow in one write (`people.invite`'s rules;
     * it supersedes the address's pending invitation). Seat setup only.
     */
    invite: PersonInvite.optional(),
  })
  .refine(
    (b) => [b.membership_id, b.invitation_id, b.invite].filter((x) => x !== undefined).length === 1,
    { message: "send to exactly one of membership_id, invitation_id and invite" },
  )
  .meta({ id: "FlowSend" });

export const FlowAgentSend = z
  .strictObject({
    flow_key: z.literal("seat_setup"),
    /** The person; they must hold `seat_id` (default: their only seat). */
    membership_id: Uuid,
    seat_id: Uuid.optional(),
  })
  .meta({ id: "FlowAgentSend" });

const StepKey = z
  .string()
  .max(64)
  .regex(/^[a-z][a-z0-9_]{0,63}$/);

export const FlowStepParams = CompanyItemParams.extend({ step: StepKey });

/** A step's values by field key (each checked against the field's type by the engine). */
export const FlowStepSave = z.strictObject({ values: BoundedJson }).meta({ id: "FlowStepSave" });

export const FlowHelperAsk = z
  .strictObject({
    step_key: StepKey,
    question: z.string().trim().min(1).max(1000),
  })
  .meta({ id: "FlowHelperAsk" });

export const FlowHelperAnswer = z
  .object({
    answer: z.string(),
    /** One value the person may accept with a click (never for a secure step). */
    suggestion: z.object({ field: z.string(), label: z.string(), value: FlowAnswer }).nullable(),
    /** `model`: the gateway answered; `help`: the step's own help text (no model configured). */
    source: z.enum(["model", "help"]),
  })
  .meta({ id: "FlowHelperAnswer" });

export const FlowSensitive = z
  .object({ values: z.record(SensitiveValueName, z.string().nullable()) })
  .meta({ id: "FlowSensitive" });

/** COLLECT-FLOWS: the collection flows (one per person-role). */
export const CollectFlowKey = z.enum([
  "collect_ceo",
  "collect_am",
  "collect_recruiter",
  "collect_sales",
  "collect_finance",
]);

/**
 * COLLECT-FLOWS: what each person still owes their agents, and what each role on the work chart
 * still waits for (`status`: the role readiness page's have / partial / missing).
 */
export const CollectionProgress = z
  .object({
    people: z.array(
      z.object({
        run_id: Uuid,
        flow_key: CollectFlowKey,
        title: z.string(),
        seat_id: Uuid,
        seat_title: z.string(),
        person_name: z.string().nullable(),
        status: z.enum(["open", "completed"]),
        total: z.int().min(0),
        answered: z.int().min(0),
        percent: z.int().min(0).max(100),
        required_total: z.int().min(0),
        required_answered: z.int().min(0),
        missing: z.array(
          z.object({
            step: z.string(),
            field: z.string(),
            label: z.string(),
            required: z.boolean(),
            used_by: z.array(z.string()),
          }),
        ),
      }),
    ),
    roles: z.array(
      z.object({
        role: z.string(),
        total: z.int().min(0),
        answered: z.int().min(0),
        status: z.enum(["have", "partial", "missing"]),
        missing: z.array(
          z.object({
            flow_key: CollectFlowKey,
            field: z.string(),
            label: z.string(),
            /** Null: no session for this person-role was sent yet. */
            seat_id: Uuid.nullable(),
            person_name: z.string().nullable(),
            required: z.boolean(),
          }),
        ),
      }),
    ),
    /** Person-roles no session was sent for yet. */
    unsent: z.array(CollectFlowKey),
  })
  .meta({ id: "CollectionProgress" });
export type CollectionProgress = z.infer<typeof CollectionProgress>;

const SAVE_EMITS = [
  "flow.step_saved",
  "seat.updated",
  "digital_employee.updated",
  "sensitive_value.sealed",
  "invite.updated",
] as const;

export const flowRoutes = {
  "flows.list": defineRoute({
    ...core,
    id: "flows.list",
    method: "get",
    path: `${base}/flows`,
    tag: "flows",
    summary: "Guided flows the caller may open (their own; managers see every run)",
    access: "viewer",
    params: CompanyParams,
    query: FlowListQuery,
    response: { status: 200, schema: FlowRunList },
    errors: [],
    emits: [],
  }),
  "flows.send": defineRoute({
    ...core,
    id: "flows.send",
    method: "post",
    path: `${base}/flows`,
    tag: "flows",
    summary: "Send a guided flow about a seat to a member or a pending invitee",
    description:
      "One open run per flow and seat: sending again is 409 with the open run as `current`.",
    access: "manager",
    params: CompanyParams,
    body: FlowSend,
    idempotency: "optional",
    response: { status: 201, schema: FlowRunSummary },
    errors: ["conflict", "seat_closed"],
    emits: ["flow.sent", "invite.created"],
  }),
  "flows.agentSend": defineRoute({
    ...core,
    id: "flows.agentSend",
    method: "post",
    path: `${base}/agent/flows`,
    tag: "flows",
    summary: "A DE sends seat setup to a person in its lanes (its supervisor, reports or peers)",
    access: "run_token",
    params: CompanyParams,
    body: FlowAgentSend,
    idempotency: "optional",
    response: { status: 201, schema: FlowRunSummary },
    errors: ["conflict", "seat_closed"],
    emits: ["flow.sent"],
  }),
  "flows.collection": defineRoute({
    ...core,
    id: "flows.collection",
    method: "get",
    path: `${base}/collection`,
    tag: "flows",
    summary: "Collection progress per person and per role (what agents still wait for)",
    description:
      "Managers see every person; anyone else sees only their own sessions. Roles list labels " +
      "and who owes them, never answers.",
    access: "viewer",
    params: CompanyParams,
    response: { status: 200, schema: CollectionProgress },
    errors: [],
    emits: [],
  }),
  "flows.get": defineRoute({
    ...core,
    id: "flows.get",
    method: "get",
    path: `${base}/flows/{id}`,
    tag: "flows",
    summary: "A guided flow as the caller sees it (answers from the real fields)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: FlowRunView },
    errors: [],
    emits: [],
  }),
  "flows.saveStep": defineRoute({
    ...core,
    id: "flows.saveStep",
    method: "put",
    path: `${base}/flows/{id}/steps/{step}`,
    tag: "flows",
    summary:
      "Save one step: each value is written to its real field (seat, settings, profile, vault)",
    access: "viewer",
    params: FlowStepParams,
    body: FlowStepSave,
    response: { status: 200, schema: FlowRunView },
    errors: ["conflict", "seat_closed", "supervisor_required", "org_chart_cycle"],
    emits: SAVE_EMITS,
  }),
  "flows.submit": defineRoute({
    ...core,
    id: "flows.submit",
    method: "post",
    path: `${base}/flows/{id}/submit`,
    tag: "flows",
    summary: "Submit from the review screen (every required field in place)",
    description:
      "Seat setup sent by a manager: when no person holds the seat, its person is assigned to it " +
      "(another person holding it is `seat_occupied`). DE onboarding: the DE's job is submitted.",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: FlowRunView },
    errors: ["conflict", "seat_occupied"],
    emits: [
      "flow.completed",
      "seat.assigned",
      "access.granted",
      "team.updated",
      "digital_employee.job_changed",
    ],
  }),
  "flows.cancel": defineRoute({
    ...core,
    id: "flows.cancel",
    method: "post",
    path: `${base}/flows/{id}/cancel`,
    tag: "flows",
    summary: "Withdraw an open guided flow",
    access: "manager",
    params: CompanyItemParams,
    response: { status: 200, schema: FlowRunSummary },
    errors: ["conflict"],
    emits: ["flow.cancelled"],
  }),
  "flows.helper": defineRoute({
    ...core,
    id: "flows.helper",
    method: "post",
    path: `${base}/flows/{id}/helper`,
    tag: "flows",
    summary: "Ask the flow's helper about a step (answers and suggests; never writes)",
    access: "viewer",
    params: CompanyItemParams,
    body: FlowHelperAsk,
    response: { status: 200, schema: FlowHelperAnswer },
    errors: [],
    emits: [],
    safe: true,
  }),
  "flows.sensitive": defineRoute({
    ...core,
    id: "flows.sensitive",
    method: "get",
    path: `${base}/flows/{id}/sensitive`,
    tag: "flows",
    summary: "The sealed pay, bank and ID values (the person, owners and finance only)",
    access: "viewer",
    params: CompanyItemParams,
    response: { status: 200, schema: FlowSensitive },
    errors: [],
    emits: [],
  }),
} as const;
